import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  Divider,
  Grid,
  useTheme,
  alpha,
  Stack,
  CircularProgress
} from '@mui/material';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Ticket,
  TrendingUp,
  ChevronRight,
  Clock
} from 'lucide-react';
import { isStrand, isBandRecommendation } from '../utils/typeGuards';
import axiosInstance from '../utils/axiosConfig';

interface MarketingAsset {
  filename: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
  description?: string;
  uploadedAt: Date;
}

interface ResourceData {
  _id: string;
  marketingAssets?: MarketingAsset[];
  mainImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
  };
  mainPosterImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    dimensions?: string;
    format?: string;
  };
  additionalPromoImages?: Array<{
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    description?: string;
  }>;
}

interface NextGigProps {
  performance: any;
}

const NextGig: React.FC<NextGigProps> = ({ performance }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [resourceData, setResourceData] = useState<ResourceData | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [hasMarketingAssets, setHasMarketingAssets] = useState(false);
  const [loadingResource, setLoadingResource] = useState(false);
  const [staffDetails, setStaffDetails] = useState<Record<string, any>>({});
  const [loadingStaffDetails, setLoadingStaffDetails] = useState(false);
  const [titoData, setTitoData] = useState<any>(null);
  const [loadingTitoData, setLoadingTitoData] = useState(false);

  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS

  // Fetch staff details
  useEffect(() => {
    const fetchMissingStaffDetails = async () => {
      if (!performance?.staff?.length) return;
      
      const staffIdsToFetch: string[] = [];
      const currentStaffDetails: Record<string, any> = {};
      
      // Check which staff need to be fetched
      performance.staff.forEach((assignment: any) => {
        const raw = assignment.staffId;
        if (typeof raw === 'string') {
          // This is just an ID, need to fetch details
          staffIdsToFetch.push(raw);
        } else if (typeof raw === 'object' && raw._id) {
          // This is already populated
          currentStaffDetails[raw._id] = raw;
        }
      });
      
      if (staffIdsToFetch.length > 0) {
        setLoadingStaffDetails(true);
        try {
          // Fetch staff details for unpopulated IDs
          const promises = staffIdsToFetch.map(id => 
            axiosInstance.get(`/api/personnel/${id}`)
          );
          
          const responses = await Promise.all(promises);
          responses.forEach((response, index) => {
            currentStaffDetails[staffIdsToFetch[index]] = response.data;
          });
          
          setStaffDetails(currentStaffDetails);
        } catch (error) {
          console.error('Error fetching staff details:', error);
        } finally {
          setLoadingStaffDetails(false);
        }
      } else {
        setStaffDetails(currentStaffDetails);
      }
    };

    fetchMissingStaffDetails();
  }, [performance?.staff, performance?._id]);

  // Fetch resource data
  useEffect(() => {
    const fetchResourceData = async () => {
      if (!performance?._id) return;
      
      setLoadingResource(true);
      try {
        const response = await axiosInstance.get(`/api/resources/performance/${performance._id}`);
        
        if (response.data && response.data.exists !== false) {
          const resource = response.data;
          setResourceData(resource);
          
          const marketingImage = getMarketingImage(resource);
          setBackgroundImage(marketingImage);
          
          const hasAssets = checkHasMarketingAssets(resource);
          setHasMarketingAssets(hasAssets);
        }
      } catch (error) {
        console.log('No resource found for performance:', performance._id);
      } finally {
        setLoadingResource(false);
      }
    };

    fetchResourceData();
  }, [performance?._id]);

  // Fetch Tito data directly since it's not in the performance object
  useEffect(() => {
    const fetchTitoData = async () => {
      if (!performance?.isTicketed || !performance?.simpleBandName) return;
      
      setLoadingTitoData(true);
      try {
        // Generate Tito slug (same logic as ActionSection)
        const artistName = performance.simpleBandName || 
          (typeof performance.band === 'object' ? performance.band?.artistName : '');
        const venueName = typeof performance.venue === 'object' ? 
          performance.venue?.name?.toLowerCase() : 'cooler';
        const date = new Date(performance.startTime);
        const month = date.toLocaleString('en-US', { month: 'short' }).toLowerCase();
        const year = date.getFullYear().toString().slice(-2);
        
        const cleanText = (text: string) => {
          return text
            .toLowerCase()
            .replace(/^the\s+/i, '')
            .replace(/\s+the\s+/g, ' ')
            .replace(/['']/g, '')
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        };
        
        const cleanArtistName = cleanText(artistName || '');
        const cleanVenueName = cleanText(venueName || '');
        const slug = `${cleanArtistName}-${cleanVenueName}-${month}${year}`;
        
        // Fetch Tito data
        const [registrationsResponse, releasesResponse] = await Promise.all([
          axiosInstance.get(`/api/tito/${slug}/registrations`),
          axiosInstance.get(`/api/tito/${slug}/releases`)
        ]);
        
        const registrations = registrationsResponse.data?.registrations || [];
        const releases = releasesResponse.data?.releases || [];
        
        // Format the data like the dashboard does
        const totalSold = releases.reduce((sum: number, release: any) => 
          sum + (parseInt(release.tickets_count) || 0), 0);
        const totalRevenue = releases.reduce((sum: number, release: any) => 
          sum + (parseInt(release.tickets_count) || 0) * (parseFloat(release.price) || 0), 0);
        
        setTitoData({
          totalSold,
          totalRevenue,
          status: 'on_sale'
        });
      } catch (error) {
        console.error('Failed to fetch Tito data for NextGig:', error);
        setTitoData({
          totalSold: 0,
          totalRevenue: 0,
          status: 'error'
        });
      } finally {
        setLoadingTitoData(false);
      }
    };

    fetchTitoData();
  }, [performance?.simpleBandName, performance?.startTime, performance?.isTicketed]);

  // Debug: Log the performance data to see what ticket info we have
  useEffect(() => {
    console.log('NextGig Debug - Performance object:', {
      performanceId: performance?._id,
      titoData: performance?.titoData,
      fetchedTitoData: titoData,
      isTicketed: performance?.isTicketed,
      revenue: performance?.revenue,
      venue: performance?.venue
    });
  }, [performance, titoData]);

  // EARLY RETURN MUST BE AFTER ALL HOOKS
  if (!performance || !performance.startTime) {
    return null;
  }

  // Enhanced time calculation logic - FIXED
  const now = new Date();
  const startDate = new Date(performance.startTime);
  const timeUntil = startDate.getTime() - now.getTime();
  
  // Calculate days properly by comparing dates, not just time difference
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const daysUntil = Math.floor((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  const hoursUntil = Math.ceil(timeUntil / (1000 * 60 * 60));
  const minutesUntil = Math.ceil(timeUntil / (1000 * 60));

  // Helper function to get marketing image
  const getMarketingImage = (resource: ResourceData): string | null => {
    let imageUrl: string | null = null;

    if (resource.marketingAssets && resource.marketingAssets.length > 0) {
      const imageAsset = resource.marketingAssets.find(asset => asset.type === 'image');
      if (imageAsset) {
        imageUrl = imageAsset.url;
      }
    }

    if (!imageUrl && resource.mainPosterImage?.url) {
      imageUrl = resource.mainPosterImage.url;
    }

    if (!imageUrl && resource.mainImage?.url) {
      imageUrl = resource.mainImage.url;
    }

    if (!imageUrl && resource.additionalPromoImages && resource.additionalPromoImages.length > 0) {
      imageUrl = resource.additionalPromoImages[0].url;
    }

    if (imageUrl) {
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      const encodedFilename = encodeURIComponent(filename);
      urlParts[urlParts.length - 1] = encodedFilename;
      return urlParts.join('/');
    }

    return null;
  };

  // Helper function to check marketing assets
  const checkHasMarketingAssets = (resource: ResourceData): boolean => {
    return !!(
      (resource.marketingAssets && resource.marketingAssets.length > 0) ||
      resource.mainImage?.url ||
      resource.mainPosterImage?.url ||
      (resource.additionalPromoImages && resource.additionalPromoImages.length > 0)
    );
  };

  // Helper functions
  const getBandName = () => {
    if (performance.simpleBandName) return performance.simpleBandName;
    if (!performance.band) return 'Unknown Artist';
    return isBandRecommendation(performance.band) ? performance.band.artistName : 'Unknown Artist';
  };

  const getStrandName = () => {
    if (!performance.strandId) return null;
    return isStrand(performance.strandId) ? performance.strandId.name : null;
  };

  const getVenueName = () => {
    if (performance.venue && typeof performance.venue === 'object' && 'name' in performance.venue) {
      return performance.venue.name;
    }
    if (performance.event && typeof performance.event === 'object' && performance.event !== null && performance.event.venue) {
      const venue = performance.event.venue;
      if (typeof venue === 'object' && venue !== null && 'name' in venue) {
        return venue.name;
      }
    }
    return 'TBA';
  };

  const getVenueCapacity = () => {
    if (performance.venue && typeof performance.venue === 'object' && 'capacity' in performance.venue) {
      return performance.venue.capacity || 0;
    }
    if (performance.event && typeof performance.event === 'object' && performance.event !== null && performance.event.venue) {
      const venue = performance.event.venue;
      if (typeof venue === 'object' && venue !== null && 'capacity' in venue) {
        return venue.capacity || 0;
      }
    }
    return performance.revenue?.expectedCapacity || 0;
  };

  // Enhanced ticket sales data - use fetched Tito data if performance.titoData is missing
  const getTicketSalesData = () => {
    // Get capacity from venue or fallback to expected
    const ticketCapacity = getVenueCapacity();
    
    // Default values when no data is available
    const defaultData = {
      ticketsSold: 0,
      ticketCapacity,
      ticketSalesPercentage: 0,
      totalRevenue: 0,
      status: 'unknown'
    };
    
    // Use fetched Tito data if performance.titoData is missing
    const currentTitoData = performance.titoData || titoData;
    
    // If we have no Tito data at all
    if (!currentTitoData) {
      return {
        ...defaultData,
        // Fallback to expected occupancy if we have it
        ticketsSold: performance.revenue?.expectedOccupancy || 0,
        ticketSalesPercentage: performance.revenue?.expectedOccupancy && ticketCapacity
          ? (performance.revenue.expectedOccupancy / ticketCapacity) * 100 
          : 0
      };
    }
    
    // If Tito data has a "not_on_sale" status
    if (currentTitoData.status === 'not_on_sale') {
      return {
        ...defaultData,
        status: 'not_on_sale'
      };
    }
    
    // If Tito data has an error status
    if (currentTitoData.status === 'error') {
      return {
        ...defaultData,
        status: 'error'
      };
    }
    
    // Use actual Tito data when available
    const ticketsSold = currentTitoData.totalSold || 0;
    return {
      ticketsSold,
      ticketCapacity,
      ticketSalesPercentage: ticketCapacity > 0 ? (ticketsSold / ticketCapacity) * 100 : 0,
      totalRevenue: currentTitoData.totalRevenue || 0,
      status: 'on_sale'
    };
  };

  const salesData = getTicketSalesData();
  const { ticketsSold, ticketCapacity, ticketSalesPercentage, totalRevenue, status } = salesData;
  
  // Check if event is ticketed (matching PerformanceCard logic)
  const isTicketed = performance.isTicketed !== false && (performance.titoData?.status !== 'not_ticketed' || titoData?.status !== 'not_ticketed');
  
  // Use combined Tito data for availability check
  const currentTitoData = performance.titoData || titoData;

  // Enhanced time display with hour/minute precision for today
  const getTimeDisplay = () => {
    if (daysUntil > 7) {
      return `${daysUntil} days`;
    } else if (daysUntil > 1) {
      return `${daysUntil} days`;
    } else if (daysUntil === 1) {
      return 'Tomorrow';
    } else if (daysUntil === 0) {
      // Today - show hours and minutes
      if (hoursUntil > 1) {
        return `${hoursUntil} hours`;
      } else if (hoursUntil === 1) {
        return '1 hour';
      } else if (minutesUntil > 0) {
        return `${minutesUntil} min`;
      } else {
        return 'Now';
      }
    } else if (hoursUntil > -6) {
      return `${Math.abs(hoursUntil)}h ago`;
    } else {
      return 'Past';
    }
  };

  const getTimeColor = () => {
    if (daysUntil > 7) return 'primary';
    if (daysUntil > 3) return 'warning';
    if (daysUntil >= 0) return 'error';
    return 'default';
  };

  // Get sales color for ticket progress (matching PerformanceCard logic)
  // Get dynamic background gradient based on ticket sales
  const getBackgroundGradient = () => {
    if (!isTicketed) {
      // Default gradient for non-ticketed events
      return backgroundImage 
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.75)}, ${alpha(theme.palette.primary.dark, 0.65)}), url(${backgroundImage})`
        : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`;
    }
    
    // Color-shifting gradients based on ticket sales percentage
    let colorGradient = '';
    
    if (ticketSalesPercentage >= 80) {
      // 💜 Legendary Status - Deep aurora magic (purple-to-electric blue)
      colorGradient = 'linear-gradient(135deg, rgba(106, 13, 173, 0.85) 0%, rgba(65, 105, 225, 0.75) 50%, rgba(30, 144, 255, 0.7) 100%)';
    } else if (ticketSalesPercentage >= 60) {
      // 🟢 Strong Sales - Cool ocean breeze (teal-to-deep blue)
      colorGradient = 'linear-gradient(135deg, rgba(32, 178, 170, 0.85) 0%, rgba(0, 139, 139, 0.75) 50%, rgba(25, 25, 112, 0.7) 100%)';
    } else if (ticketSalesPercentage >= 25) {
      // 🟠 Building Momentum - Warm sunset (orange-to-deep amber)
      colorGradient = 'linear-gradient(135deg, rgba(255, 140, 0, 0.85) 0%, rgba(255, 99, 71, 0.75) 50%, rgba(184, 134, 11, 0.7) 100%)';
    } else {
      // 🔴 Poor Sales - Deep red-to-black gradient (ember glow)
      colorGradient = 'linear-gradient(135deg, rgba(220, 20, 60, 0.85) 0%, rgba(139, 0, 0, 0.75) 50%, rgba(47, 27, 20, 0.7) 100%)';
    }
    
    // Combine color gradient with background image
    return backgroundImage 
      ? `${colorGradient}, url(${backgroundImage})`
      : colorGradient.replace(/rgba\(([^)]+),\s*[\d.]+\)/g, 'rgb($1)'); // Remove alpha for solid colors when no image
  };

  // Get sales color for progress bar (keeping the line indicator)
  const getSalesColor = () => {
    // Don't show any color if not ticketed
    if (performance.isTicketed === false || performance.titoData?.status === 'not_ticketed') {
      return 'transparent';
    }
    
    if (status === 'not_on_sale') return theme.palette.error.main;
    
    // Match the gradient colors for consistency
    if (ticketSalesPercentage >= 80) return '#6a0dad'; // Purple
    if (ticketSalesPercentage >= 60) return '#20b2aa'; // Teal
    if (ticketSalesPercentage >= 25) return '#ff8c00'; // Orange
    return '#dc143c'; // Red
  };

  const staffCount = performance.staff?.length || 0;

  return (
    <Card 
      sx={{ 
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        minHeight: 300,
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        background: getBackgroundGradient(),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        '&:hover': {
          transform: 'translateY(-6px) scale(1.01)',
          boxShadow: `0 15px 30px ${alpha(getSalesColor(), 0.4)}`,
        }
      }}
      onClick={() => navigate(`/admin/performances/${performance._id}`)}
    >
      {/* Light overlay only when there's a background image */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 100%)',
            zIndex: 1
          }}
        />
      )}

      {/* Ticket Sales Progress Bar at the top */}
      {isTicketed && (
        <LinearProgress 
          variant="determinate" 
          value={status === 'not_on_sale' ? 0 : ticketSalesPercentage} 
          sx={{ 
            height: 6, 
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            position: 'relative',
            zIndex: 2,
            '& .MuiLinearProgress-bar': {
              backgroundColor: getSalesColor(),
              boxShadow: `0 0 10px ${alpha(getSalesColor(), 0.6)}`
            }
          }} 
        />
      )}

      <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
              Next Gig
            </Typography>
            <Chip
              icon={daysUntil === 0 ? <Clock size={16} /> : undefined}
              label={getTimeDisplay()}
              color={getTimeColor()}
              size="small"
              sx={{ 
                fontWeight: 600,
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
          </Box>

          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              mb: 0.5,
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              lineHeight: 1.1
            }}
          >
            {getBandName()}
          </Typography>

          {getStrandName() && (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              {getStrandName()}
            </Typography>
          )}
        </Box>

        {/* Event Details */}
        <Grid container spacing={3} sx={{ mb: 2, flex: 1 }}>
          <Grid item xs={12} md={8}>
            {/* Main Event Info */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MapPin size={20} style={{ marginRight: 10, opacity: 0.9 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {getVenueName()}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 3.75, mb: 1.5 }}>
                <Calendar size={18} style={{ marginRight: 10, opacity: 0.9 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {startDate.toLocaleDateString('en-GB', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400 }}>
                    {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>

                 {/* Ticket Sales Info */}
              {isTicketed && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  ml: 3.75,
                  p: 2,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <Ticket size={24} style={{ marginRight: 12, opacity: 0.9 }} />
                  <Box sx={{ flex: 1 }}>
                    {!currentTitoData ? (
                      <Typography variant="body2" sx={{ color: 'rgba(255,193,7,1)' }}>
                        {loadingTitoData ? 'Loading ticket data...' : 'No Tito data available'}
                      </Typography>
                    ) : status === 'not_on_sale' ? (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        Tickets not yet on sale
                      </Typography>
                    ) : status === 'error' ? (
                      <Typography variant="body2" sx={{ color: 'rgba(255,193,7,1)' }}>
                        Ticket data error
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
                          {ticketsSold}/{ticketCapacity} sold
                        </Typography>
                        {totalRevenue > 0 && (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            €{totalRevenue.toFixed(2)} revenue
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <Box sx={{ 
                    width: 56, 
                    height: 56, 
                    borderRadius: '50%',
                    background: !currentTitoData 
                      ? 'rgba(255,193,7,0.3)' 
                      : `conic-gradient(${getSalesColor()} ${ticketSalesPercentage * 3.6}deg, rgba(255,255,255,0.2) 0deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <Box sx={{
                      width: 46,
                      height: 46,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        {!currentTitoData ? '?' : `${Math.round(ticketSalesPercentage)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Enhanced Staff Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Users size={18} style={{ marginRight: 8, opacity: 0.9 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Assigned Staff
                </Typography>
              </Box>
              
              {performance.staff && performance.staff.length > 0 ? (
                <Stack spacing={1}>  
                  {performance.staff.map((assignment: any, index: number) => {
                    let name = 'Unknown Staff';
                    let role = 'Staff Member';
                    let initial = 'S';
                    
                    // Same logic as BandEventInfo
                    const raw = assignment.staffId;
                    if (typeof raw === 'object' && raw._id) {
                      // Already populated
                      name = raw.name || 'Name not available';
                      initial = name.charAt(0);
                      role = assignment.assignedRole || 'Role pending';
                    } else if (typeof raw === 'string') {
                      // Use fetched details
                      const fetchedData = staffDetails[raw];
                      name = fetchedData?.name || 'Loading...';
                      initial = name.charAt(0);
                      role = assignment.assignedRole || 'Role pending';
                    } else {
                      // Fallback
                      name = 'Name not available';
                      role = assignment.assignedRole || 'Role pending';
                    }
                    
                    return (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 1,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1.5,
                            bgcolor: alpha(theme.palette.primary.light, 0.8),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.9rem'
                          }}
                        >
                          {initial}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {name}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'rgba(255,255,255,0.8)',
                            fontStyle: 'italic'
                          }}>
                            {role}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              ) : loadingStaffDetails ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 2
                }}>
                  <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.8)', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Loading staff details...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 2,
                  backgroundColor: 'rgba(255,193,7,0.2)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,193,7,0.3)'
                }}>
                  <Typography variant="body1" sx={{ 
                    color: 'rgba(255,193,7,1)',
                    fontWeight: 600 
                  }}>
                    No Staff Assigned
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    mt: 0.5
                  }}>
                    Staff assignment needed
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Action Button */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            fullWidth
            size="medium"
            sx={{
              py: 1,
              backgroundColor: alpha(theme.palette.common.white, 0.15),
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.25),
                transform: 'translateY(-1px)'
              }
            }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NextGig;